import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { Attachment } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

export const useCodeSnippets = (addMessage: any) => {
  const { user } = useAuth();

  // Add a code snippet with detailed logging
  const addCodeSnippet = useCallback(async (code: string, language: string, name: string, chatId?: string) => {
    if (!user) {
      console.error('[addCodeSnippet] No user found. Aborting insert.');
      toast({
        title: "Error",
        description: "You must be logged in to save code snippets.",
        variant: "destructive"
      });
      return null;
    }
    try {
      const snippetId = uuidv4();
      const insertObj = {
        id: snippetId,
        user_id: user.id,
        code,
        language,
        name,
        chat_id: chatId || null
      };
      console.log('[addCodeSnippet] Attempting to insert code snippet:', insertObj);
      const { data, error } = await supabase
        .from('code_snippets')
        .insert(insertObj)
        .select()
        .single();
      if (error) {
        console.error('[addCodeSnippet] Supabase insert error:', error);
        toast({
          title: "Error",
          description: "Failed to save code snippet. " + (error.message || ""),
          variant: "destructive"
        });
        return null;
      }
      console.log('[addCodeSnippet] Inserted code snippet:', data);

      // If we have a chatId, add the code snippet as an attachment to a message
      if (chatId) {
        const attachment: Attachment = {
          id: snippetId,
          type: 'code',
          name,
          content: code
        };
        await addMessage(chatId, {
          role: 'system',
          content: `Code snippet ${name} added`,
          sender: 'system',
          attachments: [attachment]
        });
      }

      return data?.id || null;
    } catch (error) {
      console.error('[addCodeSnippet] Unexpected error:', error);
      toast({
        title: "Error",
        description: "Failed to save code snippet. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  }, [user, addMessage]);

  // Fetch all code snippets for the current user
  const fetchCodeSnippets = useCallback(async () => {
    if (!user) return [];
    try {
      const { data, error } = await supabase
        .from('code_snippets')
        .select('*')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: false });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching code snippets:', error);
      return [];
    }
  }, [user]);

  // Update the name/title of a code snippet
  const updateCodeSnippetName = useCallback(async (snippetId: string, newName: string) => {
    if (!user) return false;
    try {
      const { error } = await supabase
        .from('code_snippets')
        .update({ name: newName })
        .eq('id', snippetId)
        .eq('user_id', user.id);
      if (error) throw error;
      toast({
        title: "Snippet renamed",
        description: "The code snippet name has been updated.",
      });
      return true;
    } catch (error) {
      console.error('Error updating code snippet name:', error);
      toast({
        title: "Rename failed",
        description: "Could not update the code snippet name.",
        variant: "destructive"
      });
      return false;
    }
  }, [user]);

  return {
    addCodeSnippet,
    fetchCodeSnippets,
    updateCodeSnippetName,
  };
};